import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { GetSupplierRelationshipDetailsResponse } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/operations/admin-manage-supplier/get-supplier-relationship-details';
import { SetBuyerPaidEarlyPaymentFeesService } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/operations/admin-manage-supplier/set-buyer-paid-early-payment-fees';
import {
    ClassNames,
    Color,
    forLifeOf,
    ignoreNullish,
    LifecycleAware,
    Modal,
    Orientation
} from '@lsq-frontend/common-ui';
import { Icons } from '@lsq-frontend/common-ui';
import { LsqValidators } from '@lsq-frontend/common-ui';
import { ModalService } from '@lsq-frontend/common-ui';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { BehaviorSubject, Subscription } from 'rxjs';
import { SaveBuyerPaidEarlyPaymentFeesModalComponent } from '../../../modals/save-buyer-paid-early-payment-fees-modal.component';
import { ManageSupplierService } from '../../manage-supplier.service';

@LifecycleAware()
@Component({
    selector: 'supplier-implementation-buyer-fee',
    styleUrls: ['./supplier-implementation-buyer-fee.component.scss'],
    template: `
        <card [headerTitle]="earlyPaymentFeesHeaderI18n" [style.marginBottom]="'3rem'">
            <div card-body *ngIf="!overrideBuyerFeesFormGroup" class="implementation-card-body">
                <lsq-circle-loader></lsq-circle-loader>
            </div>
            <div card-body *ngIf="!!overrideBuyerFeesFormGroup" class="implementation-card-body">
                <div class="fees-structure-controls">
                    <form class="control-inputs" [formGroup]="overrideBuyerFeesFormGroup">
                        <div class="control-row" [style.marginBottom]="'1.8rem'">
                            <lsq-switch
                                [labelOrientation]="orientation.Right"
                                name="overridingBuyerPaidEarlyPaymentFees"
                                [style.marginRight]="'1.5rem'"
                                id="overridingBuyerPaidEarlyPaymentFees"
                                formControlName="overridingBuyerPaidEarlyPaymentFees"
                            >
                            </lsq-switch>
                            <div [style.width]="'100%'">
                                <lsq-label [label]="buyerPaidEarlyPaymentFeesI18n"></lsq-label>
                            </div>
                        </div>

                        <div class="row ml-lg-5">
                            <div class="col-md-6 order-md-1" [style.marginBottom]="'1rem'">
                                <lsq-label
                                    [label]="buyerPercentageEarlyFeesI18n"
                                    [subLabel]="buyerPercentageEarlyFeesDescriptionI18n"
                                ></lsq-label>
                            </div>
                            <div class="col-md-6 order-md-3" [style.marginBottom]="'1rem'">
                                <lsq-input
                                    [disabled]="
                                        !overrideBuyerFeesFormGroup.get(
                                            'overridingBuyerPaidEarlyPaymentFees'
                                        ).value
                                    "
                                    name="buyerPaidEalyPaymentPercentageFees"
                                    formControlName="buyerPaidEalyPaymentPercentageFees"
                                    [group]="overrideBuyerFeesFormGroup"
                                    placeholder="Percent"
                                    [apenedIcon]="[icons.FaPercent, color.GraphicGray]"
                                    [digitsOnly]="true"
                                    [maxLength]="3"
                                    [textFormatter]="formatNoLeftZerosInput"
                                >
                                </lsq-input>
                            </div>
                            <div class="col-md-6 order-md-2" [style.marginBottom]="'1rem'">
                                <lsq-label
                                    [label]="supplierPercentageEarlyFeesI18n"
                                    [subLabel]="supplierPercentageEarlyFeesDescriptionI18n"
                                ></lsq-label>
                            </div>
                            <div class="col-md-6 order-md-4" [style.marginBottom]="'1rem'">
                                <lsq-input
                                    name="overrideBuyerPaidEarlyPaymentFees"
                                    id="overrideBuyerPaidEarlyPaymentFees"
                                    placeholder="0"
                                    [disabled]="'true'"
                                    [apenedIcon]="[icons.FaPercent, color.GraphicGray]"
                                    [inputValue]="
                                        100 -
                                        overrideBuyerFeesFormGroup.get(
                                            'buyerPaidEalyPaymentPercentageFees'
                                        ).value
                                    "
                                >
                                </lsq-input>
                            </div>
                        </div>
                    </form>
                    <lsq-button [style.marginTop]="'2rem'" (buttonClick)="openSaveModal()">
                        Save
                    </lsq-button>
                </div>
            </div>
        </card>
    `
})
export class ManageSupplierImplementationBuyerFeeComponent implements OnInit, OnDestroy {
    public readonly icons = Icons;
    public readonly color = Color;
    public readonly orientation = Orientation;

    public relationship: GetSupplierRelationshipDetailsResponse;

    public buyerPaidFeesEarlyPaymentFormSubs: Subscription;
    public overridingBuyerFeeEPSubs: Subscription;

    constructor(
        private _manageSupplierService: ManageSupplierService,
        private _modalService: ModalService,
        private _i18n: I18n,
        private _setBuyerPaidEarlyPaymentFeesService: SetBuyerPaidEarlyPaymentFeesService
    ) {}

    public ngOnInit(): void {
        this._manageSupplierService.currentSupplierRelationship$
            .pipe(forLifeOf(this), ignoreNullish())
            .subscribe((relationship) => {
                this.relationship = relationship;
                this._setupForm();
            });
    }

    public overrideBuyerFeesFormGroup: FormGroup = new FormGroup({
        overridingBuyerPaidEarlyPaymentFees: new FormControl(null),
        buyerPaidEalyPaymentPercentageFees: new FormControl(null, [
            Validators.required,
            LsqValidators.wholeNumber,
            LsqValidators.minNumber(0),
            LsqValidators.maxNumber(100)
        ])
    });

    private _setupForm(): void {
        const relationshipBuyerPaidEarlyPaymentFees =
            this.relationship.relationshipBuyerPaidEarlyPaymentFees ??
            this.relationship.buyerPaidEarlyPaymentFees;

        this.overrideBuyerFeesFormGroup.reset({
            buyerPaidEalyPaymentPercentageFees: relationshipBuyerPaidEarlyPaymentFees ?? 0,
            overridingBuyerPaidEarlyPaymentFees:
                this.relationship.relationshipBuyerPaidEarlyPaymentFees !== null
        });

        this.overrideBuyerFeesFormGroup.updateValueAndValidity();
        this.overrideBuyerFeesFormGroup.markAllAsTouched();

        this._manageSupplierService.currentSettings.buyerPaidEarlyPayment = this._manageSupplierService.currentOverridingSettings.buyerPaidEarlyPayment = Number(
            relationshipBuyerPaidEarlyPaymentFees
        );

        this._manageSupplierService.currentSettings.supplierPaidEarlyPayment = this._manageSupplierService.currentOverridingSettings.supplierPaidEarlyPayment =
            100 - relationshipBuyerPaidEarlyPaymentFees;

        this._manageSupplierService.defaultSettings.buyerPaidEarlyPayment = Number(
            this.relationship.buyerPaidEarlyPaymentFees
        );

        this._manageSupplierService.defaultSettings.supplierPaidEarlyPayment =
            100 - this.relationship.buyerPaidEarlyPaymentFees;

        this.buyerPaidFeesEarlyPaymentFormSubs = this.overrideBuyerFeesFormGroup.valueChanges
            .pipe(forLifeOf(this))
            .subscribe((form) => {
                this._manageSupplierService.currentOverridingSettings.buyerPaidEarlyPayment = Number(
                    form.buyerPaidEalyPaymentPercentageFees
                );
                this._manageSupplierService.currentOverridingSettings.supplierPaidEarlyPayment =
                    100 - form.buyerPaidEalyPaymentPercentageFees;
            });

        this.overridingBuyerFeeEPSubs = this.overrideBuyerFeesFormGroup
            .get('overridingBuyerPaidEarlyPaymentFees')
            .valueChanges.pipe(forLifeOf(this))
            .subscribe((form) => {
                if (!form.overridingBuyerPaidEarlyPaymentFees) {
                    this.overrideBuyerFeesFormGroup
                        .get('buyerPaidEalyPaymentPercentageFees')
                        .patchValue(this.relationship.buyerPaidEarlyPaymentFees ?? 0);

                    this._manageSupplierService.currentOverridingSettings.buyerPaidEarlyPayment = Number(
                        this.relationship.buyerPaidEarlyPaymentFees
                    );
                    this._manageSupplierService.currentOverridingSettings.supplierPaidEarlyPayment =
                        100 - this.relationship.buyerPaidEarlyPaymentFees;
                    this.overrideBuyerFeesFormGroup.updateValueAndValidity();
                }
            });
    }

    public ngOnDestroy(): void {
        this.buyerPaidFeesEarlyPaymentFormSubs?.unsubscribe();
        this.overridingBuyerFeeEPSubs?.unsubscribe();
    }

    public openSaveModal() {
        this.overrideBuyerFeesFormGroup.updateValueAndValidity();
        this.overrideBuyerFeesFormGroup.markAllAsTouched();

        if (this.overrideBuyerFeesFormGroup.valid) {
            const isSubmitting$ = new BehaviorSubject<boolean>(false);
            const buyerPaidEalyPaymentPercentageFees = this.overrideBuyerFeesFormGroup.get(
                'buyerPaidEalyPaymentPercentageFees'
            ).value;
            const overridingBuyerPaidEarlyPaymentFees = this.overrideBuyerFeesFormGroup.get(
                'overridingBuyerPaidEarlyPaymentFees'
            ).value;

            this._modalService.showModal(
                new Modal({
                    id: 'save-buyer-paid-early-payment-fees',
                    title: this.earlyPaymentFeesModalHeaderI18n,
                    showFooter: true,
                    showOkButton: true,
                    bodyComponent: SaveBuyerPaidEarlyPaymentFeesModalComponent,
                    data: {
                        buyerPaidPercentageEarlyPayment: buyerPaidEalyPaymentPercentageFees ?? 0,
                        supplierPaidPercentageEarlyPayment:
                            buyerPaidEalyPaymentPercentageFees !== 100
                                ? 100 - buyerPaidEalyPaymentPercentageFees
                                : 0
                    },
                    okButtonText: this.saveSettingI18n,
                    okButtonSize: ClassNames.Block,
                    showCancelButton: true,
                    cancelButtonText: this.cancelI18n,
                    isSubmitting$,
                    onOkClick: async () => {
                        isSubmitting$.next(true);
                        await this._setBuyerPaidEarlyPaymentFeesService.executeOnce({
                            relationshipKey: this.relationship.relationshipKey,
                            relationshipBuyerPaidEarlyPaymentFeePercentage: overridingBuyerPaidEarlyPaymentFees,
                            buyerPaidFeePercentage: overridingBuyerPaidEarlyPaymentFees
                                ? buyerPaidEalyPaymentPercentageFees
                                : 0
                        });
                        isSubmitting$.next(false);
                        this._modalService.closeModal();
                        await this._manageSupplierService.loadRelationship(
                            this.relationship.relationshipKey
                        );
                    },
                    onBackdropClick: async () => {
                        this._modalService.closeModal();
                    },
                    onCancelClick: async () => {
                        this._modalService.closeModal();
                    }
                })
            );
        }
    }

    public formatNoLeftZerosInput(value: number): number {
        if (value === null || typeof value === 'undefined') {
            return value;
        }
        return value * 1;
    }

    public readonly earlyPaymentFeesModalHeaderI18n = this._i18n({
        value: 'Save Early Payment Fee Settings',
        description: 'Modal header'
    });

    public readonly earlyPaymentFeesModalDescriptionI18n = this._i18n({
        value: 'The fee discount settings for this supplier will be updated to the following:',
        description: 'Modal description'
    });

    public readonly saveSettingI18n = this._i18n({
        value: 'Save Settings',
        description: 'Save modal button'
    });
    public readonly cancelI18n = this._i18n({
        value: 'Cancel',
        description: 'Modal cancel button'
    });

    public readonly earlyPaymentFeesHeaderI18n = this._i18n({
        value: 'Early Payment fees',
        description: 'Card header'
    });

    public readonly buyerPaidEarlyPaymentFeesI18n = this._i18n({
        value: 'Buyer Paid Early Payment Fees',
        description: 'Toggle name for Buyer Paid Early Payment Fees'
    });

    public readonly buyerPercentageEarlyFeesI18n = this._i18n({
        value: 'Buyer Paid Percentage of Early Payment Fees',
        description: 'Buyer percentage early payment fees field'
    });

    public readonly buyerPercentageEarlyFeesDescriptionI18n = this._i18n({
        value:
            'The percentage (between 0-100) of early payment fees the buyer will be responsible for on behalf of the supplier. A value of 100 will result in buyers being responsible for 100% of the early payment fees for all suppliers enrolled on the platform.',
        description: 'Buyer percentage early payment fees field description'
    });

    public readonly supplierPercentageEarlyFeesI18n = this._i18n({
        value: 'Supplier Paid Percentage of Early Payment Fees',
        description: 'Supplier percentage early payment fees field'
    });

    public readonly supplierPercentageEarlyFeesDescriptionI18n = this._i18n({
        value:
            'The percentage (between 0-100) of early payment fees the supplier will be responsible for. A value of 100 will result in suppliers being responsible for 100% of the early payment fees for all suppliers enrolled on the platform.',
        description: 'Supplier percentage early payment fees field description'
    });
}
